/**
 * Hook do status da ultima integracao Zeus (Task #63).
 *
 * Padrao do useProximo6du.ts: cache module-level + refetch periodico.
 * Aqui o TTL eh 5min porque o badge precisa refletir o sync logo apos
 * o cron das 7h BRT (em vez das 24h do calendario CCEE).
 *
 * Em caso de falha (rede/auth/5xx) devolve um payload "red - sem dados"
 * sintetico — frontend nao quebra mesmo se a tabela sync_runs estiver
 * vazia ou o endpoint cair.
 */
import { useEffect, useState } from "react";
import { getLastSync } from "../api/client";
import type { LastSyncResponse } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 5 * 60 * 1000; // 5 minutos

interface CacheEntry {
  expiresAt: number;
  promise: Promise<LastSyncResponse>;
}

let cache: CacheEntry | null = null;

function fallback(): LastSyncResponse {
  return {
    by_source: {},
    primary: "zeus_api",
    primary_status_band: "red",
    primary_status_reason: "sem dados",
  };
}

function fetchLastSync(): Promise<LastSyncResponse> {
  return getLastSync().catch(() => fallback());
}

export function invalidateLastSyncCache(): void {
  cache = null;
}

export interface UseLastSyncState {
  data: LastSyncResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function useLastSync(): UseLastSyncState {
  const [state, setState] = useState<UseLastSyncState>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;

    function load() {
      const now = Date.now();
      if (!cache || cache.expiresAt < now) {
        cache = { expiresAt: now + REFETCH_MS, promise: fetchLastSync() };
      }
      cache.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          setState({ data: null, isLoading: false, error: err });
        });
    }

    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, []);

  return state;
}
